import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const EditVoucher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [formData, setFormData] = useState({
    type: 'receipt',
    voucher_date: new Date().toISOString().split('T')[0],
    account_id: '',
    amount: '',
    description: '',
    notes: '',
    payment_method: 'cash',
    check_number: '',
    bank_name: '',
    reference_number: '',
    currency: 'SAR',
    exchange_rate: '1'
  });

  useEffect(() => {
    fetchVoucher();
    fetchAccounts();
  }, [id]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/vouchers/${id}`);
      if (response.data.status) {
        const voucherData = response.data.data;
        setVoucher(voucherData);
        setFormData({
          type: voucherData.type,
          voucher_date: voucherData.voucher_date,
          account_id: voucherData.account_id,
          amount: voucherData.amount,
          description: voucherData.description,
          notes: voucherData.notes || '',
          payment_method: voucherData.payment_method,
          check_number: voucherData.check_number || '',
          bank_name: voucherData.bank_name || '',
          reference_number: voucherData.reference_number || '',
          currency: voucherData.currency || 'SAR',
          exchange_rate: voucherData.exchange_rate || '1'
        });
      }
    } catch (error) {
      console.error('Error fetching voucher:', error);
      alert('حدث خطأ أثناء تحميل بيانات السند');
      navigate('/vouchers');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/vouchers/accounts');
      if (response.data.status) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await axios.put(`/api/vouchers/${id}`, formData);
      if (response.data.status) {
        alert('تم تحديث السند بنجاح');
        navigate('/vouchers');
      }
    } catch (error) {
      console.error('Error updating voucher:', error);
      alert('حدث خطأ أثناء تحديث السند');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/vouchers');
  };

  if (loading) {
    return (
      <div className="vouchers-container">
        <div className="loading-container">
          <div className="loading-spinner">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="vouchers-container">
        <div className="error-container">
          <h2>السند غير موجود</h2>
          <button onClick={handleCancel} className="btn btn-primary">
            العودة إلى السندات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vouchers-container">
      <div className="page-header">
        <div className="page-title">
          <h3>{t('accounting.vouchers.editVoucher')}</h3>
          <p>تعديل بيانات السند رقم {voucher.voucher_number}</p>
        </div>
        <button 
          type="button" 
          className="back-btn"
          onClick={handleCancel}
        >
          ← {t('accounting.vouchers.actions.back')}
        </button>
      </div>

      <div className="form-container">
        <div className="help-panels">
          <div className="help-panel">
            <div className="panel-header">
              <span className="info-icon">ℹ️</span>
              <h6>معلومات السند</h6>
            </div>
            <div className="panel-content">
              <div className="help-item">
                <strong>رقم السند:</strong>
                <p>{voucher.voucher_number}</p>
              </div>
              <div className="help-item">
                <strong>الحالة:</strong>
                <p>{t(`accounting.vouchers.voucherStatus.${voucher.status}`)}</p>
              </div>
              <div className="help-item">
                <strong>النوع:</strong>
                <p>{t(`accounting.vouchers.voucherTypes.${voucher.type}`)}</p>
              </div>
              <div className="help-item">
                <strong>التاريخ:</strong>
                <p>{voucher.voucher_date}</p>
              </div>
            </div>
          </div>

          <div className="guide-panel">
            <div className="panel-header">
              <span className="info-icon">📋</span>
              <h6>ملاحظات التعديل</h6>
            </div>
            <div className="panel-content">
              <div className="help-item">
                <strong>السندات المرحلة:</strong>
                <p>لا يمكن تعديل السندات المرحلة</p>
              </div>
              <div className="help-item">
                <strong>السندات المسودة:</strong>
                <p>يمكن تعديل جميع البيانات</p>
              </div>
              <div className="help-item">
                <strong>السندات الملغية:</strong>
                <p>لا يمكن تعديل السندات الملغية</p>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <form onSubmit={handleSubmit} className="account-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.type')}</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                  disabled={voucher.status !== 'draft'}
                >
                  <option value="receipt">{t('accounting.vouchers.voucherTypes.receipt')}</option>
                  <option value="payment">{t('accounting.vouchers.voucherTypes.payment')}</option>
                </select>
                <div className="form-note">نوع السند</div>
              </div>

              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.voucherDate')}</label>
                <input
                  type="date"
                  name="voucher_date"
                  value={formData.voucher_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                  disabled={voucher.status !== 'draft'}
                />
                <div className="form-note">تاريخ السند</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.account')}</label>
                <select
                  name="account_id"
                  value={formData.account_id}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                  disabled={voucher.status !== 'draft'}
                >
                  <option value="">اختر الحساب</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))}
                </select>
                <div className="form-note">الحساب المرتبط بالسند</div>
              </div>

              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.amount')}</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  step="0.01"
                  className="form-control"
                  placeholder="0.00"
                  disabled={voucher.status !== 'draft'}
                />
                <div className="form-note">مبلغ السند</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">{t('accounting.vouchers.description')}</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="form-control"
                rows="3"
                placeholder="وصف السند"
                disabled={voucher.status !== 'draft'}
              />
              <div className="form-note">وصف تفصيلي للسند</div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.paymentMethod')}</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                  disabled={voucher.status !== 'draft'}
                >
                  <option value="cash">{t('accounting.vouchers.paymentMethods.cash')}</option>
                  <option value="bank_transfer">{t('accounting.vouchers.paymentMethods.bank_transfer')}</option>
                  <option value="check">{t('accounting.vouchers.paymentMethods.check')}</option>
                  <option value="card">{t('accounting.vouchers.paymentMethods.card')}</option>
                </select>
                <div className="form-note">طريقة الدفع</div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('accounting.vouchers.referenceNumber')}</label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="رقم المرجع"
                  disabled={voucher.status !== 'draft'}
                />
                <div className="form-note">رقم المرجع (اختياري)</div>
              </div>
            </div>

            {formData.payment_method === 'check' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('accounting.vouchers.checkNumber')}</label>
                  <input
                    type="text"
                    name="check_number"
                    value={formData.check_number}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="رقم الشيك"
                    disabled={voucher.status !== 'draft'}
                  />
                  <div className="form-note">رقم الشيك</div>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('accounting.vouchers.bankName')}</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="اسم البنك"
                    disabled={voucher.status !== 'draft'}
                  />
                  <div className="form-note">اسم البنك المصدر للشيك</div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('accounting.vouchers.notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="ملاحظات إضافية"
                disabled={voucher.status !== 'draft'}
              />
              <div className="form-note">ملاحظات إضافية (اختياري)</div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
                disabled={saving}
              >
                {t('accounting.vouchers.actions.cancel')}
              </button>
              {voucher.status === 'draft' && (
                <button
                  type="submit"
                  className="btn-save"
                  disabled={saving}
                >
                  {saving ? 'جاري الحفظ...' : t('accounting.vouchers.actions.save')}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditVoucher;
